import { useCallback } from "react";

/** `data-overflow-end` on the scroller: "true" while columns are hidden past the right edge. */
const TABLE_SCROLL_OVERFLOW_END_ATTR = "overflowEnd";
/** Visible scroller width, for content pinned to the viewport (empty message). */
const TABLE_SCROLL_WIDTH_VAR = "--table-scroll-width";

/**
 * Callback ref for a horizontal table scroller.
 * - Shift+wheel scrolls horizontally. Native overflow-x already handles
 *   trackpad deltaX — do not intercept it (would fight the browser).
 * - Keeps `data-overflow-end` in sync so pinned columns can draw a divider
 *   only while content scrolls underneath them, and `--table-scroll-width`
 *   for content pinned to the visible area (no React re-render).
 * A callback ref (not `useRef` + effect) so the listeners attach whenever the
 * scroller mounts, e.g. after a loading state.
 */
export function useHorizontalTrackpadScroll<T extends HTMLElement>() {
  return useCallback((element: T | null) => {
    if (!element) {
      return;
    }

    const updateOverflow = () => {
      const overflowEnd =
        element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
      element.dataset[TABLE_SCROLL_OVERFLOW_END_ATTR] = String(overflowEnd);
      element.style.setProperty(
        TABLE_SCROLL_WIDTH_VAR,
        `${element.clientWidth}px`,
      );
    };

    const onWheel = (event: WheelEvent) => {
      if (!event.shiftKey || event.deltaY === 0) {
        return;
      }
      if (element.scrollWidth <= element.clientWidth) {
        return;
      }

      event.preventDefault();
      element.scrollLeft += event.deltaY;
    };

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(element);
    if (element.firstElementChild) {
      resizeObserver.observe(element.firstElementChild);
    }
    element.addEventListener("wheel", onWheel, { passive: false });
    element.addEventListener("scroll", updateOverflow, { passive: true });
    updateOverflow();

    return () => {
      resizeObserver.disconnect();
      element.removeEventListener("wheel", onWheel);
      element.removeEventListener("scroll", updateOverflow);
    };
  }, []);
}
